import React, { useId } from "react";
import {
  Box,
  IconButton,
  MenuItem,
  MenuList,
  Popover,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

/**
 * Controlled dropdown with a separate heading and a single selected value.
 * Options: { value, label, description?, disabled? }.
 * Use mode="navigate" for category rows. The caller handles closing or changing
 * the displayed options in onSelect(value). onBack adds header navigation.
 */
export default function DropdownMenu({
  anchorEl,
  open,
  onClose,
  title,
  description,
  options = [],
  value,
  onSelect,
  mode = "select",
  onBack,
  backLabel = "Back",
  emptyText = "No options available",
}) {
  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;
  const selecting = mode === "select";

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{
        sx: {
          mt: 0.5,
          width: 240,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "min(400px, calc(100% - 32px))",
          display: "flex",
          flexDirection: "column",
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
          overflow: "hidden",
        },
      }}
    >
      <Stack
        component='header'
        direction='row'
        alignItems='center'
        gap={0.75}
        sx={{
          px: 1.5,
          py: 1,
          minHeight: 40,
          boxSizing: "border-box",
          flexShrink: 0,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          cursor: "default",
        }}
      >
        {onBack && (
          <IconButton
            size='small'
            aria-label={backLabel}
            onClick={onBack}
            sx={{
              p: 0.5,
              color: "inherit",
              borderRadius: 1,
              border: "1px solid",
              borderColor: (theme) =>
                alpha(theme.palette.primary.contrastText, 0.25),
              bgcolor: (theme) =>
                alpha(theme.palette.primary.contrastText, 0.08),
              "&:hover": {
                bgcolor: (theme) =>
                  alpha(theme.palette.primary.contrastText, 0.18),
              },
              "&.Mui-focusVisible": {
                outline: "2px solid currentColor",
                outlineOffset: 2,
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            id={titleId}
            component='h2'
            variant='caption'
            color='inherit'
            fontWeight={700}
            sx={{
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              overflowWrap: "anywhere",
            }}
          >
            {title}
          </Typography>
          {description && (
            <Typography
              id={descriptionId}
              variant='caption'
              display='block'
              sx={{
                mt: 0.25,
                color: (theme) =>
                  alpha(theme.palette.primary.contrastText, 0.8),
              }}
            >
              {description}
            </Typography>
          )}
        </Box>
      </Stack>
      <MenuList
        key={`${mode}-${title}`}
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        autoFocusItem={open}
        autoFocus={open && !options.some((option) => !option.disabled)}
        variant={selecting ? "selectedMenu" : "menu"}
        onKeyDown={(event) => {
          if (event.key === "Tab") {
            event.preventDefault();
            onClose?.(event, "tabKeyDown");
          }
        }}
        sx={{
          p: 0.5,
          pt: 0.75,
          minHeight: 0,
          overflowY: "auto",
          bgcolor: "background.paper",
        }}
      >
        {options.map((option) => {
          const selected = selecting && Object.is(value, option.value);
          return (
            <MenuItem
              key={option.value}
              role={selecting ? "menuitemradio" : "menuitem"}
              aria-checked={selecting ? selected : undefined}
              selected={selected}
              disabled={option.disabled}
              onClick={() => onSelect(option.value)}
              sx={{
                gap: 1,
                px: 1,
                py: 0.75,
                my: 0.25,
                minHeight: 36,
                "@media (pointer: coarse)": { minHeight: 44 },
                whiteSpace: "normal",
                borderRadius: 1,
                border: "1px solid transparent",
                "&.Mui-selected": {
                  color: "primary.main",
                  borderColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.35),
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  "&:hover": {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                  },
                },
                "&.Mui-focusVisible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: -2,
                },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
                <Typography
                  variant='body2'
                  fontWeight={selected ? 700 : 500}
                  sx={{ fontSize: 13 }}
                >
                  {option.label}
                </Typography>
                {option.description && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    display='block'
                  >
                    {option.description}
                  </Typography>
                )}
              </Box>
              {selecting ? (
                <Box
                  aria-hidden='true'
                  sx={{
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: "50%",
                    border: "1px solid",
                    borderColor: selected ? "primary.main" : "divider",
                    bgcolor: selected ? "primary.main" : "transparent",
                    color: "primary.contrastText",
                  }}
                >
                  {selected && <CheckIcon sx={{ fontSize: 13 }} />}
                </Box>
              ) : (
                <ChevronRightIcon
                  fontSize='small'
                  sx={{ color: "text.secondary", fontSize: 18 }}
                />
              )}
            </MenuItem>
          );
        })}
        {options.length === 0 && (
          <Typography
            component='li'
            role='presentation'
            variant='body2'
            color='text.secondary'
            sx={{ p: 1.5 }}
          >
            {emptyText}
          </Typography>
        )}
      </MenuList>
    </Popover>
  );
}
